import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import { extra, secondary, text } from '@/theme/colors/palettes';
import { Center, HStack, Input, Text } from '@chakra-ui/react';
import { useState } from 'react';

type Props = {
  placeholder: string;
  minChars?: number;
  maxChars?: number;
  minHeight?: number | string;
  value?: string;
  initialSubmittedValue?: string;
  isEditableSubmittedValue?: boolean;
  inputWidth?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onDeleteSubmitted?: () => void;
  onCancel?: () => void;
};

const InlineAddTextField = ({
  placeholder,
  minChars = 2,
  maxChars = 160,
  minHeight = 53,
  value: controlledValue,
  initialSubmittedValue = '',
  isEditableSubmittedValue = false,
  inputWidth = '90%',
  onChange,
  onSubmit,
  onDeleteSubmitted,
  onCancel,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(controlledValue ?? initialSubmittedValue);
  const [submittedValue, setSubmittedValue] = useState(initialSubmittedValue);

  const value = controlledValue ?? internalValue;
  const displayValue = submittedValue || value;
  const isSubmitted = isEditableSubmittedValue && !!submittedValue;
  const submitDisabled = value.length < minChars || value.length > maxChars;
  const showInput = isEditing || isSubmitted;
  const showSubmitButton = !isSubmitted;
  const showDeleteButton = isSubmitted && !!onDeleteSubmitted;

  const setValue = (newValue: string) => {
    setInternalValue(newValue);
    if (isSubmitted && isEditing) {
      setSubmittedValue(newValue);
    }
    onChange?.(newValue);
  };

  const startEditing = () => {
    setIsEditing(true);
    if (isSubmitted) {
      setValue(submittedValue);
    }
  };

  const stopEditing = () => {
    setIsEditing(false);
    if (!isSubmitted) {
      setValue('');
    }
    onCancel?.();
  };

  const submitValue = () => {
    if (submitDisabled) return;

    if (!isSubmitted) {
      onSubmit?.(value);
    }

    if (isEditableSubmittedValue) {
      setSubmittedValue(value);
    } else {
      setValue('');
    }

    setIsEditing(false);
  };

  const deleteSubmittedValue = () => {
    setSubmittedValue('');
    setValue('');
    setIsEditing(false);
    onDeleteSubmitted?.();
  };

  return (
    <HStack
      onClick={startEditing}
      width={'100%'}
      padding={'10px 16px'}
      justify={'space-between'}
      alignItems={'center'}
      rounded={'10px'}
      minHeight={minHeight}
      border={`1px solid ${extra.mediumBlue15pct}`}
      backgroundColor={extra.white}
      style={
        isEditing
          ? { boxShadow: `0px 2px 4px 0px ${extra.mediumBlue20pct}`, borderColor: extra.mediumBlue40pct }
          : { boxShadow: `0px 2px 4px 0px ${extra.mediumBlue5pct}` }
      }
      _hover={{ boxShadow: `0px 2px 4px 0px ${extra.mediumBlue15pct} !important`, borderColor: extra.mediumBlue30pct }}
      transition={'0.3s'}
      cursor={'pointer'}
    >
      {showInput ? (
        <Input
          autoFocus={isEditing}
          maxLength={maxChars}
          value={isSubmitted && !isEditing ? displayValue : value}
          onChange={(e) => setValue(e.target.value)}
          borderWidth={0}
          padding={0}
          maxHeight={'28px'}
          focusBorderColor={'transparent'}
          placeholder={placeholder}
          color={'text.mediumBlue'}
          fontWeight={600}
          fontSize={14}
          width={showSubmitButton || showDeleteButton ? inputWidth : '100%'}
          readOnly={isSubmitted && !isEditing}
          _placeholder={{ fontWeight: '600', color: 'text.mediumGray', fontSize: 14 }}
          onClickCapture={(e) => {
            if (!isSubmitted || isEditing) {
              e.stopPropagation();
            }
          }}
          onBlur={() => {
            if (isEditing && !isSubmitted) {
              stopEditing();
            } else {
              setIsEditing(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.code === 'Escape') {
              stopEditing();
            }

            if (e.code === 'Enter') {
              e.preventDefault();
              submitValue();
            }
          }}
        />
      ) : (
        <Text variant={'urbanistSemiBold'} fontSize={14} color={text.mediumGray}>
          {placeholder}
        </Text>
      )}

      {showSubmitButton && (
        <Center
          padding={'6px'}
          rounded={'5px'}
          style={{
            backgroundColor: submitDisabled ? secondary[300] : secondary[500],
            boxShadow: '0px 2px 4px 0px #0041790D',
          }}
          cursor={submitDisabled ? 'not-allowed' : 'pointer'}
          onMouseDown={
            submitDisabled
              ? (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  submitValue();
                }
          }
        >
          <SvgIcon iconPath={svgIcons.plus} color="#fff" size={16} />
        </Center>
      )}

      {showDeleteButton && (
        <Center
          padding={'6px'}
          rounded={'5px'}
          cursor={'pointer'}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteSubmittedValue();
          }}
        >
          <SvgIcon iconPath={svgIcons.trashCan} color={extra.red} size={14} boxWidth={12} boxHeight={14} />
        </Center>
      )}
    </HStack>
  );
};

export default InlineAddTextField;
